import { Column } from "@/components/CellGrid/Column";
import { DSCRCell } from "@/components/CellGrid/DSCRCell";
import { defaultLabelColumnWidth } from "@/components/CellGrid/LabelColumn";
import { Row } from "@/components/CellGrid/Row";
import { lineSize } from "@/components/CellGrid/Waterfall";
import { defaultHeight } from "@/components/CellGrid/cell/Wrapper";
import { Doc } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import { cloneFromEpochsAtom } from "@/financeModels/owasco/clone";
import {
  companySaleIncludedAtom,
  companySaleYearAtom,
} from "@/financeModels/owasco/general";
import { iterationCountAtom } from "@/financeModels/owasco/iterations";
import {
  dscrMolecule,
  growthRateModeAtom,
  yearsFromSbaLoanStartDateAtom,
} from "@/financeModels/owasco/projections";
import { useIsModelReadOnly } from "@/hooks/useFinancialDeckPermissions";
import { ZIndexOrder } from "@/theme";
import {
  Box,
  BoxComponentProps,
  Button,
  Group,
  Menu,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { ReactNode, memo } from "react";
import { FaBuildingCircleArrowRight } from "react-icons/fa6";
import { IoArrowRedo } from "react-icons/io5";
import { LuCalendarClock } from "react-icons/lu";
import { TbCaretUpDownFilled, TbTableImport } from "react-icons/tb";
import { ProjectionYearModal } from "./ProjectionYearModal";

export const IterationRow = ({ ...props }: BoxComponentProps) => {
  const [isModelReadOnly] = useIsModelReadOnly();
  const years = useAtomValue(yearsFromSbaLoanStartDateAtom);
  const saleYear = useAtomValue(companySaleYearAtom);
  const isCompanySale = useAtomValue(companySaleIncludedAtom);
  const dscrAtoms = useAtomValue(dscrMolecule);
  const iterationCount = useAtomValue(iterationCountAtom);
  const clone = useSetAtom(cloneFromEpochsAtom);
  const projectionModalDisclosure = useDisclosure();
  const openImportHisoricalsModal = () =>
    modals.openConfirmModal({
      title: "Import Historical Data",
      children: (
        <Text size="sm">
          Importing data from your historicals table will overwrite the base
          year for your projection table. This cannot be undone. Proceed?
        </Text>
      ),
      labels: { confirm: "Overwrite Projection Base Year", cancel: "Cancel" },
      onConfirm: clone,
    });

  return (
    <>
      <Row
        {...props}
        style={{
          position: "sticky",
          top: 0,
          zIndex: ZIndexOrder.CellGridIterationRow,
          borderBottom: "1px solid #ddd",
        }}
        bg={"gray.3"}
      >
        <Group
          gap={"xs"}
          style={{
            width: defaultLabelColumnWidth + lineSize,
          }}
        >
          <Button
            size="compact-sm"
            leftSection={<TbTableImport size={14} />}
            variant="outline"
            onClick={openImportHisoricalsModal}
          >
            Import From Historicals
          </Button>
        </Group>
        {dscrAtoms.map((atom, i) =>
          i === 1 ? (
            <Column key={`${i}`} justify="center" align={"center"}>
              <GrowthRateCell />
            </Column>
          ) : (
            <Column key={`${i}`}>
              <DSCRCell
                docPath="@/docs/pages/model/projections/dscr.mdx"
                dscrAtom={atom}
                {...(i === 0 ? { bg: "white" } : {})}
                style={{
                  ...(isCompanySale && saleYear === i - 1
                    ? {
                        boxShadow:
                          "inset 0px -2px 0px var(--mantine-color-infomatic-5)",
                      }
                    : {}),
                }}
              >
                <Group gap={4}>
                  <Stack gap={1}>
                    <Text lh={1} h={14} fz={"md"} fw={500}>
                      Year {i < 1 ? i : i - 1}
                    </Text>
                    <Text
                      fz={"xs"}
                      style={{
                        fontSize: 9,
                      }}
                      h={10}
                      lh={1}
                    >
                      {i < 1 ? (
                        <LabelExplainer
                          lh={"inherit"}
                          inline
                          label="Base Year"
                          explanation={
                            <Doc path="@/docs/pages/model/projections/base-year.mdx" />
                          }
                        />
                      ) : (
                        (years[i - 2] as ReactNode)
                      )}
                    </Text>
                  </Stack>{" "}
                  {isCompanySale && saleYear === i - 1 && (
                    <Tooltip label="Company Sale Year">
                      <ThemeIcon variant="transparent" color="infomatic.5">
                        <FaBuildingCircleArrowRight size={22} />
                      </ThemeIcon>
                    </Tooltip>
                  )}
                </Group>
              </DSCRCell>
            </Column>
          ),
        )}
        {!isModelReadOnly && (
          <Button
            leftSection={
              iterationCount ? (
                <LuCalendarClock size={22} />
              ) : (
                <IoArrowRedo size={22} />
              )
            }
            h={defaultHeight}
            onClick={projectionModalDisclosure[1].open}
            justify="left"
            mx="sm"
            //variant="default"
          >
            {!iterationCount ? "Create Projection Table" : "Change Projection"}
          </Button>
        )}
        <ProjectionYearModal disclosure={projectionModalDisclosure} />
      </Row>
    </>
  );
};

const GrowthRateCell = memo(() => {
  const [mode, setMode] = useAtom(growthRateModeAtom);

  return (
    <Menu shadow="md" withArrow arrowSize={10} offset={4}>
      <Menu.Target>
        <Stack
          justify="center"
          align={"center"}
          w={"100%"}
          style={
            {
              //boxShadow: "inset 0px -1px 0px var(--mantine-color-gray-5)",
            }
          }
          gap={0}
          p={4}
        >
          <Button
            color="accent"
            w={"100%"}
            //justify="space-between"
            ta={"center"}
            radius={4}
            pos={"relative"}
          >
            Growth Rate (%)
            <br />
            {mode === "compoundAnnual" ? "CAGR" : "Per-Year"}
            <Box pos={"absolute"} right={4} top={"33%"}>
              <TbCaretUpDownFilled />
            </Box>
          </Button>
        </Stack>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>
          <LabelExplainer
            inline
            showIcon
            label={"Choose a growth rate mode: "}
            explanation={
              <Doc path="@/docs/pages/model/projections/growth-rate-header.mdx" />
            }
            zIndex={1000}
          />
        </Menu.Label>
        <Menu.Divider />
        <Menu.Item onClick={() => setMode("compoundAnnual")}>
          Compound Annual (CAGR)
        </Menu.Item>
        <Menu.Item onClick={() => setMode("perYear")}>Per-Year</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
});
